/// <amd-module name="Release/WorkItemReleaseLinks/ReleaseLinkStatutsTextService" />
import * as Resources from "Release/WorkItemReleaseLinks/Resources";
import { ApprovalType, ComputedReleaseEnvironmentStatus, IReleaseDomainService } from "./ReleaseDomainService";
import { format } from "VSS/Core/Util/String";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { IVssService, VssService, Services, IVssPageContext } from "VSS/Platform/Context";
import { localeDateFormat } from "VSS/Platform/Date";
import { ReleaseEnvironment } from "Release/Client/Contracts/Release";

export interface IReleaseLinkStatutsTextService extends IVssService {
    getStatusToolTipText(
        environment: ReleaseEnvironment,
        isReleaseDeleted?: boolean
    ): string | undefined;
}

class ReleaseLinkStatutsTextService
    extends VssService
    implements IReleaseLinkStatutsTextService {
    private releaseDomainService: IReleaseDomainService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.releaseDomainService = this.pageContext.getService<IReleaseDomainService>("IReleaseDomainService");
    }

    public getStatusToolTipText(
        environment: ReleaseEnvironment,
        isReleaseDeleted?: boolean
    ): string | undefined {
        const maxAttemptObject = this.releaseDomainService.getLatestDeployment(environment.deploySteps);
        const status = this.releaseDomainService.getComputedEnvironmentStatusFromDeployment(maxAttemptObject);

        var maxAttempt: number = this.releaseDomainService.getMaxAttemptNumberFromDeploySteps(
            environment.deploySteps
        );
        switch (status) {
            case ComputedReleaseEnvironmentStatus.Deferred:
                var deferredTime = localeDateFormat(
                    getDateInUserTimeZone(environment.scheduledDeploymentTime as Date)
                );
                return format(
                    Resources.DeferredDeploymentStatusToolTipFormat,
                    deferredTime
                );
            case ComputedReleaseEnvironmentStatus.Pending:
                if (!isReleaseDeleted) {
                    if (
                        this.releaseDomainService.environmentHasPendingApproval(
                            environment,
                            ApprovalType.PostApproval,
                            maxAttempt
                        )
                    ) {
                        return Resources.PendingPostDeploymentApproval;
                    } else if (this.releaseDomainService.hasManualIntervention(environment)) {
                        return Resources.PendingManualIntervention;
                    }
                } else {
                    return Resources.ApprovalPendingForDeletedReleaseTooltipText;
                }
            case ComputedReleaseEnvironmentStatus.PreApprovalPending:
                if (
                    this.releaseDomainService.environmentHasPendingApproval(
                        environment,
                        ApprovalType.PreApproval,
                        maxAttempt
                    ) &&
                    !isReleaseDeleted
                ) {
                    return Resources.PendingPreDeploymentApproval;
                } else {
                    return Resources.ApprovalPendingForDeletedReleaseTooltipText;
                }
            case ComputedReleaseEnvironmentStatus.Queued:
                return Resources.QueueForDeploymentTooltipText;
            case ComputedReleaseEnvironmentStatus.QueuedForAgent:
                return Resources.QueueForAgentTooltipText;
            case ComputedReleaseEnvironmentStatus.QueuedForPipeline:
                return Resources.QueueForPipelineTooltipText;
            case ComputedReleaseEnvironmentStatus.InProgress:
                return Resources.DeploymentInProgressTooltipText;
            case ComputedReleaseEnvironmentStatus.Rejected:
                return Resources.DeploymentPostApprovalRejectedTooltipText;
            case ComputedReleaseEnvironmentStatus.PreApprovalRejected:
                return Resources.DeploymentPreApprovalRejectedTooltipText;
            case ComputedReleaseEnvironmentStatus.Scheduled:
                var scheduledTime = localeDateFormat(
                    getDateInUserTimeZone(environment.scheduledDeploymentTime as Date)
                );
                if (!isReleaseDeleted) {
                    return format(
                        Resources.ScheduleForDeploymentTooltipText,
                        scheduledTime
                    );
                } else {
                    return format(
                        Resources.ScheduleForDeploymentForDeletedReleaseTooltipText,
                        scheduledTime
                    );
                }

            case ComputedReleaseEnvironmentStatus.PartiallySucceeded:
                return Resources.DeploymentPartiallySucceededTooltipText;
        }

        return this.getStatusTextNew(status);
    }

    private getStatusTextNew(
        status: ComputedReleaseEnvironmentStatus
    ): string | undefined {
        switch (status) {
            case ComputedReleaseEnvironmentStatus.Succeeded:
                return Resources.SummaryEnvironmentStatusSucceeded;
            case ComputedReleaseEnvironmentStatus.Pending:
            case ComputedReleaseEnvironmentStatus.PreApprovalPending:
                return Resources.SummaryEnvironmentStatusPending;
            case ComputedReleaseEnvironmentStatus.NotStarted:
                return Resources.SummaryEnvironmentStatusNotDeployed;
            case ComputedReleaseEnvironmentStatus.Queued:
            case ComputedReleaseEnvironmentStatus.QueuedForAgent:
            case ComputedReleaseEnvironmentStatus.QueuedForPipeline:
                return Resources.SummaryEnvironmentStatusQueued;
            case ComputedReleaseEnvironmentStatus.Rejected:
            case ComputedReleaseEnvironmentStatus.PreApprovalRejected:
                return Resources.SummaryEnvironmentStatusRejected;
            case ComputedReleaseEnvironmentStatus.InProgress:
                return Resources.SummaryEnvironmentStatusInprogressNew;
            case ComputedReleaseEnvironmentStatus.Deferred:
                return Resources.SummaryEnvironmentStatusDeferred;
            case ComputedReleaseEnvironmentStatus.Canceled:
                return Resources.SummaryEnvironmentStatusCancelled;
            case ComputedReleaseEnvironmentStatus.Failed:
                return Resources.SummaryEnvironmentStatusFailed;
            case ComputedReleaseEnvironmentStatus.Scheduled:
                return Resources.SummaryEnvironmentStatusScheduled;
            case ComputedReleaseEnvironmentStatus.PartiallySucceeded:
                return Resources.SummaryEnvironmentStatusPartiallySucceeded;
            case ComputedReleaseEnvironmentStatus.Cancelling:
                return Resources.SummaryEnvironmentStatusCancelling;
            case ComputedReleaseEnvironmentStatus.GatesInProgress:
                return Resources.SummaryGatesStatusInProgress;
            case ComputedReleaseEnvironmentStatus.GatesFailed:
                return Resources.SummaryGatesStatusFailed;
            case ComputedReleaseEnvironmentStatus.GatesStabilizing:
                return Resources.SummaryGatesStatusStabilizing;
        }
    }
}

Services.add("IReleaseLinkStatutsTextService", {
    serviceFactory: ReleaseLinkStatutsTextService,
});
